import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierEnrichmentProfileEditForEntityUrl,
    supplierEnrichmentProfileEditUrl,
    supplierEnrichmentProfileUrl,
    supplierEntityDetailUrl,
    supplierEntityLogoUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {EnrichmentProfile} from "~/pages/supplier-360/types/Supplier360Types";

export type UpdateSupplierEnrichmentVariables = {
    /** The entity the profile belongs to, so its query can be refreshed. */
    entityId: number;
    /** The current `EnrichmentProfile` id, or undefined when the entity has never been enriched. */
    profileId?: number;
    /** Written to `contact_info.domain`, but sent as a top-level `domain` key (§2.5). */
    domain: string;
};

export function useUpdateSupplierEnrichmentMutation() {
    const queryClient = useQueryClient();

    return useMutation<EnrichmentProfile, DefaultError, UpdateSupplierEnrichmentVariables>({
        mutationFn: async ({entityId, profileId, domain}) => {
            const body = {domain};

            if (profileId !== undefined) {
                const {data} = await queryHttp.patch<EnrichmentProfile>(
                    supplierEnrichmentProfileEditUrl(profileId),
                    body,
                );
                return data;
            }

            const {data} = await queryHttp.post<EnrichmentProfile>(
                supplierEnrichmentProfileEditForEntityUrl(),
                {entity: entityId, ...body},
            );
            return data;
        },
        onSuccess: (created, variables) => {
            queryClient.setQueryData([supplierEnrichmentProfileUrl(variables.entityId)], created);
            // The edit response is the new profile. The logo is already resolved on the entity
            // (§2.5), but `logo_url` lives on the detail and registry caches, and the image query
            // keeps its bytes for an hour — refresh both or the header and registry stay stale.
            void queryClient.invalidateQueries({
                queryKey: [supplierEntityDetailUrl(variables.entityId)],
            });
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('supplier_360/entity/?');
                },
            });
            void queryClient.resetQueries({
                queryKey: [supplierEntityLogoUrl(variables.entityId)],
            });
            notificationStore.show('Domain updated.', {
                severity: 'success',
                id: `supplier-enrichment-success-${variables.entityId}`,
                autoHideDuration: 3000,
            });
        },
    });
}
